import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Card, Loading } from "../components/UI";
import { Save, Building2, Banknote, Tag, Download, Upload, AlertTriangle } from "lucide-react";
import { formatApiError } from "../lib/format";
import { useAuth } from "../lib/auth";

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snapExporting, setSnapExporting] = useState(false);
  const [snapImporting, setSnapImporting] = useState(false);
  const [snapMode, setSnapMode] = useState("upsert");
  const [snapResult, setSnapResult] = useState(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/settings").then(r => { setS(r.data); setLoading(false); }); }, []);

  const update = (k, v) => setS(prev => ({...prev, [k]: v}));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {...s};
      delete payload._id; delete payload.created_at; delete payload.updated_at;
      const r = await api.patch("/settings", payload);
      setS(r.data);
      toast.success("Settings saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading || !s) return <Loading/>;

  const isAdmin = user?.role === "admin";
  const envLabel = (s.environment || "preview").toUpperCase();

  const exportSnapshot = async () => {
    setSnapExporting(true);
    try {
      const res = await api.get("/admin/snapshot/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `modav-snapshot-${envLabel.toLowerCase()}-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Snapshot exported from ${envLabel}`);
    } catch (err) { toast.error(formatApiError(err) || "Export failed"); }
    finally { setSnapExporting(false); }
  };

  const importSnapshot = async (file) => {
    if (!file) return;
    if (snapMode === "replace" && !confirmReplace) {
      toast.error("Tick the confirmation checkbox to use REPLACE mode");
      return;
    }
    setSnapImporting(true);
    setSnapResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/admin/snapshot/import?mode=${snapMode}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSnapResult(res.data.summary);
      toast.success(`Snapshot imported into ${envLabel} (${snapMode})`);
      setConfirmReplace(false);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) { toast.error(formatApiError(err) || "Import failed"); }
    finally { setSnapImporting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="fade-up flex justify-between items-end">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Settings</h1>
          <p className="text-[#5E7166] text-sm">Configure brand, contacts, payments and operational defaults.</p>
        </div>
        <button onClick={save} disabled={saving} className="btn-gold" data-testid="save-settings-btn"><Save size={14}/> {saving ? "Saving…" : "Save Changes"}</button>
      </div>

      <Card title="Property Information" testid="settings-property">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Property Name" value={s.property_name} onChange={(v) => update("property_name", v)}/>
          <Field label="Currency Symbol" value={s.currency_symbol || "₦"} onChange={(v) => update("currency_symbol", v)}/>
          <div className="md:col-span-2"><Field label="Property Address" value={s.property_address} onChange={(v) => update("property_address", v)}/></div>
          <Field label="City" value={s.city} onChange={(v) => update("city", v)}/>
          <Field label="State" value={s.state} onChange={(v) => update("state", v)}/>
          <Field label="Logo URL" value={s.logo_url} onChange={(v) => update("logo_url", v)}/>
          <Field label="Tagline" value={s.tagline} onChange={(v) => update("tagline", v)}/>
        </div>
      </Card>

      <Card title="Key Personnel" testid="settings-personnel">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Landlord" value={s.landlord_name} onChange={(v) => update("landlord_name", v)}/>
          <Field label="Property Manager" value={s.manager_name} onChange={(v) => update("manager_name", v)}/>
          <Field label="Legal Officer" value={s.legal_officer_name} onChange={(v) => update("legal_officer_name", v)}/>
          <Field label="Manager Direct Phone" value={s.manager_phone || ""} onChange={(v) => update("manager_phone", v)} placeholder="+234 803 123 4567"/>
        </div>
        <p className="text-xs text-[#5E7166] mt-2">Manager phone shows on the public landing page contact card. Falls back to the active manager's staff record if blank.</p>
      </Card>

      <Card title="WhatsApp Contacts (Public Landing Page)" testid="settings-whatsapp">
        <p className="text-xs text-[#5E7166] mb-3">Visitors who submit the contact form on <b>modavhomes.com</b> can continue the conversation directly on WhatsApp. Their typed message is pre-filled in the chat. Use international format (e.g. <code>+234 803 123 4567</code> or <code>08031234567</code>).</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Manager WhatsApp" value={s.manager_whatsapp || ""} onChange={(v) => update("manager_whatsapp", v)} placeholder="+234 803 123 4567"/>
          <Field label="Admin WhatsApp" value={s.admin_whatsapp || ""} onChange={(v) => update("admin_whatsapp", v)} placeholder="+234 813 456 7890"/>
        </div>
      </Card>

      <Card title="Promotional Banner (Public Landing Page)" testid="settings-banner">
        <p className="text-xs text-[#5E7166] mb-3">Optional banner shown at the very top of <b>modavhomes.com</b>. Use it to advertise a unit, run a promotion, or share an announcement. Visitors can dismiss it; refreshing the page brings it back.</p>
        <div className="flex items-center justify-between bg-[#F8FBF9] border border-[#E5EBE8] rounded-lg p-3 mb-3">
          <div>
            <div className="font-semibold text-[#1A2E24] text-sm">Banner active</div>
            <div className="text-xs text-[#5E7166]">Toggle off to instantly hide on the public site.</div>
          </div>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={!!s.promo_banner_active}
              onChange={(e) => update("promo_banner_active", e.target.checked)}
              className="sr-only peer"
              data-testid="promo-banner-active-toggle"
            />
            <div className="w-11 h-6 bg-[#E5EBE8] peer-checked:bg-[#0E5C3A] rounded-full peer transition-colors relative">
              <div className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow transition-transform ${s.promo_banner_active ? "translate-x-5" : ""}`}></div>
            </div>
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Banner Title" value={s.promo_banner_title || ""} onChange={(v) => update("promo_banner_title", v)} placeholder="Hall now available for lease"/>
          <Field label="Banner Body (short)" value={s.promo_banner_body || ""} onChange={(v) => update("promo_banner_body", v)} placeholder="Reserve before 31 March for 5% off"/>
          <Field label="CTA Label" value={s.promo_banner_cta_label || ""} onChange={(v) => update("promo_banner_cta_label", v)} placeholder="Enquire now"/>
          <Field label="CTA Link" value={s.promo_banner_cta_url || ""} onChange={(v) => update("promo_banner_cta_url", v)} placeholder="#contact or https://wa.me/..."/>
        </div>
      </Card>

      <Card title="Rent Bank Account (Admin-managed)" testid="settings-rent-bank">
        <p className="text-xs text-[#5E7166] mb-3">Tenants pay <b>rent only</b> to this account. Displayed on rent invoices, receipts, and tenant payment pages.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Bank Name" value={s.rent_bank_name} onChange={(v) => update("rent_bank_name", v)}/>
          <Field label="Account Name" value={s.rent_bank_account_name} onChange={(v) => update("rent_bank_account_name", v)}/>
          <Field label="Account Number" value={s.rent_bank_account_number} onChange={(v) => update("rent_bank_account_number", v)}/>
        </div>
      </Card>

      <Card title="Other Fees Bank Account (Manager-managed)" testid="settings-fees-bank">
        <p className="text-xs text-[#5E7166] mb-3">Caution, agreement, service charges, utility bills, etc. flow to this account.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Bank Name" value={s.fees_bank_name} onChange={(v) => update("fees_bank_name", v)}/>
          <Field label="Account Name" value={s.fees_bank_account_name} onChange={(v) => update("fees_bank_account_name", v)}/>
          <Field label="Account Number" value={s.fees_bank_account_number} onChange={(v) => update("fees_bank_account_number", v)}/>
        </div>
      </Card>

      <Card title="Email Delivery (Brevo)" testid="settings-brevo">
        <p className="text-xs text-[#5E7166] mb-3">Configure Brevo (formerly Sendinblue) for password reset, lease invites, and payment receipts. Add <code>BREVO_API_KEY</code> + <code>BREVO_SENDER_EMAIL</code> to <code>backend/.env</code> to enable real emails. Without keys, emails are logged only.</p>
        <Field label="Sender Email" value={s.brevo_sender_email || ""} onChange={(v) => update("brevo_sender_email", v)} placeholder="noreply@modavhomes.ng"/>
      </Card>

      <Card title="Google Sign-In" testid="settings-google">
        <p className="text-xs text-[#5E7166] mb-3">Allows users to sign in with Google. Paste your OAuth Client ID from Google Cloud Console.</p>
        <Field label="Google Client ID" value={s.google_client_id || ""} onChange={(v) => update("google_client_id", v)} placeholder="xxx.apps.googleusercontent.com"/>
      </Card>

      <Card title="Payments" testid="settings-payments">
        <p className="text-xs text-[#5E7166] bg-[#FEF0F0]/40 p-3 rounded-lg">Online payment gateways have been removed. Tenants pay via bank transfer / POS and upload receipts for manager approval.</p>
      </Card>

      <Card title="Lease & Reminders" testid="settings-lease">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Default Lease Term (months)" value={s.lease_term_months} onChange={(v) => update("lease_term_months", Number(v))} type="number"/>
          <Field label="Reminder Days (comma-separated)" value={(s.reminder_days || [60,30,7]).join(",")} onChange={(v) => update("reminder_days", v.split(",").map(x => Number(x.trim())).filter(Boolean))}/>
        </div>
      </Card>

      <Card title="Data Privacy & Backup">
        <div className="text-sm text-[#5E7166] space-y-1">
          <p>• All tenant documents stored in encrypted object storage.</p>
          <p>• Role-based access enforced on all API endpoints.</p>
          <p>• Every change is logged in the audit log.</p>
          <p>• Backup: Use MongoDB exports — automatic backups will be available in a future release.</p>
        </div>
      </Card>

      {isAdmin && (
        <Card title="Environment Snapshot" testid="settings-snapshot">
          <div className="space-y-4">
            <div className="text-sm text-[#5E7166]">
              Current environment: <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider ${s.environment === "production" ? "bg-[#0E5C3A] text-white" : "bg-[#C92A2A] text-white"}`}>{envLabel}</span>
              <span className="ml-2">— export user-entered data to JSON, then import on another environment to mirror it. Excludes credentials, settings, property, pricing and unit_types.</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="card-modav p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-[#C9A227] mb-2">Export from {envLabel}</div>
                <p className="text-xs text-[#5E7166] mb-3">Downloads tenants, leases, payments, units status, maintenance, communications, audit logs and more as a single JSON file. Safe to run anytime.</p>
                <button
                  onClick={exportSnapshot}
                  disabled={snapExporting}
                  className="btn-outline-emerald text-sm inline-flex items-center gap-1.5 w-full justify-center"
                  data-testid="snapshot-export-btn"
                >
                  <Download size={14}/> {snapExporting ? "Preparing…" : "Export Snapshot JSON"}
                </button>
              </div>

              <div className="card-modav p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-[#C9A227] mb-2">Import into {envLabel}</div>
                <p className="text-xs text-[#5E7166] mb-3">Upload a previously-exported snapshot. <b>Upsert</b> merges with existing data (safe). <b>Replace</b> wipes the target collections first (clean clone — destructive).</p>

                <div className="flex items-center gap-3 mb-2 text-sm">
                  <label className="inline-flex items-center gap-1.5">
                    <input type="radio" checked={snapMode === "upsert"} onChange={() => { setSnapMode("upsert"); setConfirmReplace(false); }} data-testid="snapshot-mode-upsert"/>
                    Upsert (safe)
                  </label>
                  <label className="inline-flex items-center gap-1.5">
                    <input type="radio" checked={snapMode === "replace"} onChange={() => setSnapMode("replace")} data-testid="snapshot-mode-replace"/>
                    Replace (destructive)
                  </label>
                </div>

                {snapMode === "replace" && (
                  <label className="flex items-start gap-2 text-xs text-[#C92A2A] bg-[#FEF0F0] p-2 rounded mb-2">
                    <input type="checkbox" checked={confirmReplace} onChange={(e) => setConfirmReplace(e.target.checked)} data-testid="snapshot-confirm-replace"/>
                    <span>I understand this will delete all current tenants, leases, payments, tickets, etc. in {envLabel} before importing.</span>
                  </label>
                )}

                <input
                  ref={fileRef}
                  type="file"
                  accept=".json,application/json"
                  onChange={(e) => importSnapshot(e.target.files?.[0])}
                  disabled={snapImporting}
                  className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-[#0E5C3A] file:text-white file:cursor-pointer hover:file:bg-[#0E5C3A]/90"
                  data-testid="snapshot-import-file"
                />
                {snapImporting && <div className="text-xs text-[#5E7166] mt-2">Importing… (large files can take 30–60s)</div>}
              </div>
            </div>

            {snapResult && (
              <div className="card-modav p-4 bg-[#F8FBF9]">
                <div className="text-xs font-bold uppercase tracking-wider text-[#0E5C3A] mb-2">Last Import Summary</div>
                <div className="text-xs text-[#5E7166] mb-2">
                  Mode: <b>{snapResult.mode}</b> &nbsp; Source: <b>{snapResult.source_env || "—"}</b> &nbsp; Generated: <b>{(snapResult.generated_at || "").slice(0, 16)}</b>
                </div>
                <div className="overflow-x-auto">
                  <table className="text-xs w-full">
                    <thead><tr className="text-left text-[#5E7166]"><th className="py-1 pr-3">Collection</th><th className="py-1 pr-3">Inserted</th><th className="py-1 pr-3">Updated</th><th className="py-1 pr-3">Wiped</th><th className="py-1">In Snapshot</th></tr></thead>
                    <tbody>
                      {Object.entries(snapResult.collections || {}).map(([k, v]) => (
                        <tr key={k} className="border-t border-[#E5EBE8]">
                          <td className="py-1 pr-3 font-mono">{k}</td>
                          <td className="py-1 pr-3">{v.skipped ? <span className="text-[#5E7166] italic">{v.reason}</span> : v.inserted}</td>
                          <td className="py-1 pr-3">{v.skipped ? "—" : v.updated}</td>
                          <td className="py-1 pr-3">{v.skipped ? "—" : v.deleted_before_insert}</td>
                          <td className="py-1">{v.skipped ? "—" : v.total_in_snapshot}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex items-start gap-2 text-xs text-[#5E7166] bg-[#FDF6E3]/60 p-3 rounded">
              <AlertTriangle size={14} className="text-[#A0801C] mt-0.5 shrink-0"/>
              <span>
                <b>How to clone prod → preview:</b> Open https://modavhomes.com/settings (admin), click <b>Export Snapshot</b>, then come back here in preview, pick the file, choose <b>Replace</b> mode (tick the confirm box), and upload. Preview will mirror prod data exactly. Excluded: credentials, environment vars, property profile, pricing table, unit types.
              </span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">{label}</span>
      <input type={type} value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="input-modav mt-1"/>
    </label>
  );
}
